import React, { Fragment, useMemo, useRef, useState, useEffect } from 'react';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import MessageBlock from './MessageBlock';
import ThinkingBlock from './ThinkingBlock';
import UntrustedSourceNote from './UntrustedSourceNote';
import MultiActionCard from './MultiActionWriteCard';
import ContactScrapeCard from './ContactScrapeCard';
import FeedbackInline from './FeedbackInline';
import WriteCard from './WriteCard';
import DelegationChip, {
	extractDelegations,
	extractDelegationCompletion,
	DelegationResultHeader,
	type DelegationData
} from '../AIWorkspace/components/DelegationChip';
import HandoffSeedMessage, { isHandoffSeed } from '../AIWorkspace/components/HandoffSeedMessage';
import DelegatedApprovalCards from '../AIWorkspace/components/DelegatedApprovalCards';
import WriteProposalGroupCard from './WriteProposalGroupCard';
import EmailDraftCard from './EmailDraftCard';
import OfferDraftCard from './OfferDraftCard';
import ToolProposalCard from './ToolProposalCard';
import ProspectingAddCard from './ProspectingAddCard';
import ChartCard from './ChartCard';
import RunSection from './RunSection';
import SuggestedReplies from './SuggestedReplies';
import ProspectingFiltersCard from './ProspectingFiltersCard';
import CoverageCard from './CoverageCard';
import LoadingBar from './LoadingBar';
import AssistantResource from 'Resources/AssistantChat';
import { type AgentRun } from 'Resources/AgentRuns';
import T from 'Components/Helpers/translate';
import type {
	AssistantMessage,
	MultiActionConfig,
	ContactScrapeConfig,
	WriteProposal,
	EmailDraft,
	OfferDraft,
	ToolProposal,
	ProspectingAddConfig
} from './types';

const readAgentRunId = (m: AssistantMessage): number | null => {
	const raw = (m.metadata as { agentRunId?: unknown } | undefined)?.agentRunId;
	if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
	return null;
};

/** First backing agent_run id referenced by a run group's messages, if any.
 *  Used to look up the run's header metadata (WEB-6797). */
const groupRunId = (g: RunGroup): number | null => {
	for (const m of g.messages) {
		const id = readAgentRunId(m);
		if (id != null) return id;
	}
	return null;
};

type RunGroup = {
	runTimestamp: string;
	messages: AssistantMessage[];
	offset: number;
};

const dayKey = (d: Date): string => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const formatDateDivider = (dateStr: string): string => {
	const d = new Date(dateStr);
	if (Number.isNaN(d.getTime())) return '';
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);
	const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
	if (msgDay.getTime() === today.getTime()) return T('date.today') || 'Today';
	if (msgDay.getTime() === yesterday.getTime()) return T('assistant.yesterday') || 'Yesterday';
	const sevenDaysAgo = new Date(today);
	sevenDaysAgo.setDate(today.getDate() - 6);
	if (msgDay >= sevenDaysAgo) {
		return d.toLocaleDateString(undefined, { weekday: 'long' });
	}
	const sameYear = d.getFullYear() === now.getFullYear();
	return d.toLocaleDateString(
		undefined,
		sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' }
	);
};

type RunDateBucket = { label: string; runs: RunGroup[] };

// Bucket runs by when they fired — Today / Yesterday / This week / Last week /
// Earlier — mirroring the conversation sidebar so long run histories stay
// scannable.
function groupRunsByDate(runs: RunGroup[]): RunDateBucket[] {
	const now = new Date();
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterdayStart = new Date(todayStart);
	yesterdayStart.setDate(yesterdayStart.getDate() - 1);
	const dayOfWeek = todayStart.getDay();
	const daysFromMonday = (dayOfWeek + 6) % 7;
	const weekStart = new Date(todayStart);
	weekStart.setDate(weekStart.getDate() - daysFromMonday);
	const lastWeekStart = new Date(weekStart);
	lastWeekStart.setDate(lastWeekStart.getDate() - 7);

	const today: RunGroup[] = [];
	const yesterday: RunGroup[] = [];
	const thisWeek: RunGroup[] = [];
	const lastWeek: RunGroup[] = [];
	const older: RunGroup[] = [];

	for (const run of runs) {
		const d = new Date(run.runTimestamp);
		if (Number.isNaN(d.getTime())) {
			older.push(run);
			continue;
		}
		if (d >= todayStart) today.push(run);
		else if (d >= yesterdayStart) yesterday.push(run);
		else if (d >= weekStart) thisWeek.push(run);
		else if (d >= lastWeekStart) lastWeek.push(run);
		else older.push(run);
	}

	const buckets: RunDateBucket[] = [];
	// Ordered oldest → newest so runs grow downward (new runs appear at the
	// bottom alongside the latest activity, older ones remain scrollable above).
	if (older.length) buckets.push({ label: T('assistant.earlier'), runs: older });
	if (lastWeek.length) buckets.push({ label: T('assistant.lastWeek') || 'Last week', runs: lastWeek });
	if (thisWeek.length) buckets.push({ label: T('assistant.thisWeek'), runs: thisWeek });
	if (yesterday.length) buckets.push({ label: T('assistant.yesterday'), runs: yesterday });
	if (today.length) buckets.push({ label: T('date.today'), runs: today });
	return buckets;
}

function groupMessagesByRun(messages: AssistantMessage[]): RunGroup[] {
	const groups: RunGroup[] = [];
	const byTimestamp = new Map<string, RunGroup>();
	let current: RunGroup | null = null;

	for (let i = 0; i < messages.length; i++) {
		const msg = messages[i];
		const runMarker = msg.metadata?.runMarker as boolean | undefined;
		const runTimestamp = msg.metadata?.runTimestamp as string | undefined;
		const runId = msg.metadata?.runId as string | undefined;

		if (runMarker || (msg.role === 'user' && !runId)) {
			const ts = runTimestamp || msg.regDate || '';
			current = { runTimestamp: ts, messages: [msg], offset: i };
			groups.push(current);
			if (ts) byTimestamp.set(ts, current);
		} else if (runId && byTimestamp.has(runId)) {
			byTimestamp.get(runId)!.messages.push(msg);
		} else if (runId) {
			current = { runTimestamp: runId, messages: [msg], offset: i };
			groups.push(current);
			byTimestamp.set(runId, current);
		} else if (current) {
			current.messages.push(msg);
		} else {
			if (!groups.length) {
				groups.push({ runTimestamp: msg.regDate || '', messages: [], offset: 0 });
			}
			groups[groups.length - 1].messages.push(msg);
		}
	}

	return groups;
}

// A run group with no run linkage on any message (no runMarker / runId /
// runTimestamp / agentRunId) is a historical orphan — a message saved before
// routine runs were tracked, with no run behind it. It has nothing to expand
// into and sorts as an unclickable row above the date groups, so drop it from
// the run history. User-authored messages are always kept.
function shouldShowRunGroup(group: RunGroup): boolean {
	return group.messages.some(m => {
		const meta = m.metadata as Record<string, unknown> | undefined;
		return (
			m.role === 'user' || !!meta?.runMarker || !!meta?.runId || !!meta?.runTimestamp || meta?.agentRunId != null
		);
	});
}

type MentionAgent = { id: number; name: string };

type Props = {
	messages: AssistantMessage[];
	streamingText: string;
	conversationId: number | null;
	avatarUrl?: string | null;
	avatarColor?: string;
	assistantName?: string;
	schedulePrompt?: string | null;
	runBased?: boolean;
	// Drop the centred 720px readable track and use the full pane width. The
	// Pages authoring chat opts in — it's a wide split pane showing code diffs
	// that benefit from every pixel (WEB-7757). The main chat keeps the track.
	fullWidth?: boolean;
	// Sticky diff open-state, owned by the parent. When provided, a message's
	// diff is open iff its id is in the set — so it survives new messages and
	// user toggles instead of auto-collapsing. Absent → fall back to "latest
	// only" (main chat, which has no diffs anyway).
	openDiffIds?: Set<number>;
	onDiffToggle?: (key: number, open: boolean) => void;
	sending?: boolean;
	toolStatus?: string;
	activeRunId?: string | null;
	streamingThinking?: string;
	/**
	 * Renders a surface-specific activity list above a reply — the Pages build
	 * ledger (WEB-10046). Deliberately a render prop over `unknown`: the thread is
	 * shared with the main chat, which has no such concept, so the item shape stays
	 * the caller's business and the main chat's render path is byte-identical
	 * (it passes neither prop, so nothing here runs).
	 * `live` distinguishes the in-flight turn from a settled one.
	 */
	renderActivities?: (activities: unknown, live: boolean) => React.ReactNode;
	/** Forwarded to the streaming MessageBlock — see MessageBlock's own prop. */
	openDiffWhileStreaming?: boolean;
	/**
	 * Marker drawn above a reply — the Pages version chip (WEB-10047). Like
	 * `renderActivities`, the thread only calls it; what it draws is the caller's
	 * business, and a surface that passes nothing renders exactly as before.
	 */
	renderTurnMarker?: (message: AssistantMessage) => React.ReactNode;
	/**
	 * Drawn above a USER message, inside the same right-aligned column as the
	 * attachment chips — the Pages picked-element chip (WEB-10047). Same contract
	 * as `renderTurnMarker`: the thread only calls it, and a surface that passes
	 * nothing renders exactly as before.
	 */
	renderUserAccessory?: (message: AssistantMessage) => React.ReactNode;
	/** The in-flight turn's activities, handed straight back to `renderActivities`. */
	streamingActivities?: unknown;
	onMultiActionUpdate?: (id: string, updates: Partial<MultiActionConfig>) => void;
	onContactScrapeUpdate?: (id: string, updates: Partial<ContactScrapeConfig>) => void;
	onWriteProposalUpdate?: (index: number, update: Partial<WriteProposal>) => void;
	onWriteProposalGroupItemUpdate?: (messageIndex: number, itemIndex: number, update: Partial<WriteProposal>) => void;
	onApprovalsGroupEmailItemUpdate?: (messageIndex: number, itemIndex: number, update: Partial<EmailDraft>) => void;
	onEmailDraftUpdate?: (index: number, update: Partial<EmailDraft>) => void;
	onOfferDraftUpdate?: (index: number, update: Partial<OfferDraft>) => void;
	onToolProposalUpdate?: (index: number, update: Partial<ToolProposal>) => void;
	onProspectingAddUpdate?: (index: number, update: Partial<ProspectingAddConfig>) => void;
	onSendFollowUp?: (text: string, runId: string, files?: File[]) => void;
	onRegenerate?: (messageIndex: number) => void | Promise<void>;
	onRetryLast?: () => void | Promise<void>;
	onStop?: () => void;
	agents?: MentionAgent[];
	onAgentClick?: (id: number) => void;
	onTaskClick?: (id: number) => void;
	onRoutineClick?: (id: number) => void;
	/** WEB-6800 — open a delegated child conversation (from a DelegationChip) in the drawer. */
	onOpenDelegation?: (delegation: DelegationData) => void;
	/** WEB-6797 — agent_run header metadata for the RunSection header. `byId`
	 *  resolves groups whose messages carry `agentRunId`; `byMarker` (keyed by
	 *  trigger runTimestamp/runId) resolves routine-fired runs that don't. */
	runsById?: Map<number, AgentRun>;
	runsByMarker?: Map<string, AgentRun>;
	/** Pre-localized provenance label for this conversation (from contextType). */
	provenance?: string | null;
};

const MessageThread = ({
	messages,
	streamingText,
	conversationId,
	avatarUrl,
	avatarColor,
	assistantName,
	schedulePrompt,
	runBased,
	fullWidth,
	openDiffIds,
	onDiffToggle,
	sending,
	toolStatus,
	activeRunId,
	streamingThinking,
	renderActivities,
	streamingActivities,
	openDiffWhileStreaming,
	renderTurnMarker,
	renderUserAccessory,
	onMultiActionUpdate,
	onContactScrapeUpdate,
	onWriteProposalUpdate,
	onWriteProposalGroupItemUpdate,
	onApprovalsGroupEmailItemUpdate,
	onEmailDraftUpdate,
	onOfferDraftUpdate,
	onToolProposalUpdate,
	onProspectingAddUpdate,
	onSendFollowUp,
	onRegenerate,
	onRetryLast,
	onStop,
	agents,
	onAgentClick,
	onTaskClick,
	onRoutineClick,
	onOpenDelegation,
	runsById,
	runsByMarker,
	provenance
}: Props) => {
	// Expand state for the current turn's thinking block, shared between the live
	// streaming block and the finished message it becomes — so expanding while it
	// thinks doesn't snap shut when it finishes. Reset each new run.
	const [liveThinkingExpanded, setLiveThinkingExpanded] = useState(false);
	const prevSendingRef = useRef(false);
	useEffect(() => {
		if (sending && !prevSendingRef.current) setLiveThinkingExpanded(false);
		prevSendingRef.current = !!sending;
	}, [sending]);
	const lastAssistantMsgIdx = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'assistant') return i;
		}
		return -1;
	}, [messages]);

	// A failed write card can hand the problem back to the agent by asking on the
	// user's behalf — same channel a suggested reply uses (WEB-9873). Undefined
	// when the host gave us no way to send, so the card hides the control.
	const askAgent = onSendFollowUp ? (text: string) => onSendFollowUp(text, '') : undefined;

	const handleFeedback = async (
		messageIndex: number,
		type: 'up' | 'down',
		reason: string
	): Promise<{ saved: boolean; savedValue?: string } | null> => {
		const assistantMsg = messages[messageIndex];
		const userMsg = messages[messageIndex - 1];
		try {
			const result = await AssistantResource.submitFeedback({
				type,
				reason,
				assistantMessage: assistantMsg?.content,
				userMessage: userMsg?.content,
				conversationId: conversationId ?? undefined
			});
			return { saved: result?.saved ?? false, savedValue: result?.savedValue };
		} catch {
			return null;
		}
	};

	// The retry-the-last-message affordance should appear whenever the most
	// recent USER message has gone unanswered — not only when the user message
	// is literally the last array element. A failed or empty run can leave a
	// trailing contentless assistant message (no text, no card) both live and
	// on reload (history keeps empty assistant rows); it renders as nothing, so
	// visually the user's message is still the last thing on screen, yet a naive
	// `last.role === 'user'` check would wrongly hide the retry button.
	const lastUserUnanswered = useMemo(() => {
		let lastUserIdx = -1;
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'user') {
				lastUserIdx = i;
				break;
			}
		}
		if (lastUserIdx === -1) return false;
		for (let i = lastUserIdx + 1; i < messages.length; i++) {
			const m = messages[i];
			const isSubstantiveReply =
				m.role === 'assistant' &&
				(!!m.content?.trim() ||
					!!m.chart ||
					!!m.prospectingFilters ||
					!!m.prospectingCoverage ||
					!!m.multiAction ||
					!!m.contactScrape ||
					!!m.emailDraft ||
					!!m.offerDraft ||
					!!m.toolProposal ||
					!!m.prospectingAdd ||
					!!m.writeProposal ||
					!!m.writeProposalGroup);
			if (isSubstantiveReply) return false;
		}
		return true;
	}, [messages]);

	const isRunBasedConversation = runBased ?? !!schedulePrompt;
	const runGroups = isRunBasedConversation ? groupMessagesByRun(messages).filter(shouldShowRunGroup) : [];
	const runBuckets = useMemo(
		() => (isRunBasedConversation ? groupRunsByDate(runGroups) : []),
		[isRunBasedConversation, runGroups]
	);
	const lastRunTimestamp = runGroups.length ? runGroups[runGroups.length - 1].runTimestamp : null;
	// The bucket that contains the latest run stays open by default; older
	// buckets collapse so long run histories don't flood the view. Clicking a
	// bucket header toggles it.
	const latestBucketLabel = useMemo(() => {
		if (!lastRunTimestamp) return null;
		const bucket = runBuckets.find(b => b.runs.some(r => r.runTimestamp === lastRunTimestamp));
		return bucket?.label ?? null;
	}, [runBuckets, lastRunTimestamp]);
	const [collapsedBuckets, setCollapsedBuckets] = useState<Set<string>>(new Set());
	const userToggledRef = useRef(false);
	useEffect(() => {
		if (!runBuckets.length) return;
		setCollapsedBuckets(new Set(runBuckets.filter(b => b.label !== latestBucketLabel).map(b => b.label)));
	}, [latestBucketLabel, runBuckets.length]);
	const toggleBucket = (label: string) => {
		userToggledRef.current = true;
		setCollapsedBuckets(prev => {
			const next = new Set(prev);
			if (next.has(label)) next.delete(label);
			else next.add(label);
			return next;
		});
	};

	const isCardMessage = (m: AssistantMessage) =>
		!!m.writeProposalGroup ||
		!!m.writeProposal ||
		!!m.multiAction ||
		!!m.contactScrape ||
		!!m.emailDraft ||
		!!m.offerDraft ||
		!!m.toolProposal ||
		!!m.prospectingAdd;
	const lastUserIdx = (() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === 'user') return i;
		}
		return -1;
	})();
	const trailingCardStart = lastUserIdx >= 0 ? messages.findIndex((m, i) => i > lastUserIdx && isCardMessage(m)) : -1;
	// Only hoist cards to the bottom while the turn is still "card-tail" — i.e.
	// the AI hasn't sent a text reply after the card yet. Once a non-card
	// message follows (e.g. the assistant's confirmation after an Accept), the
	// timeline must stay chronological so the new reply lands BELOW the card.
	const lastIsCard = messages.length > 0 && isCardMessage(messages[messages.length - 1]);
	const trailingCards: Array<{ m: AssistantMessage; i: number }> =
		!lastIsCard || trailingCardStart === -1
			? []
			: messages
					.slice(trailingCardStart)
					.map((m, j) => ({ m, i: trailingCardStart + j }))
					.filter(({ m }) => isCardMessage(m));
	// If the current turn already has a persisted intro text (i.e. we're now
	// streaming a follow-up reply after an Accept), the streaming bubble must
	// render BELOW the trailing card — otherwise the reply jumps "above the
	// card → below the card" as it transitions from streaming to persisted.
	const hasIntroTextInTurn = (() => {
		for (let i = lastUserIdx + 1; i < messages.length; i++) {
			const m = messages[i];
			if (m.role === 'assistant' && !isCardMessage(m) && m.content?.trim()) return true;
		}
		return false;
	})();
	const streamingRendersAfterCards = hasIntroTextInTurn;

	return (
		<div
			className={cn(
				'flex flex-col w-full px-5 pt-5',
				fullWidth ? '' : 'max-w-[720px] mx-auto',
				isRunBasedConversation ? 'pb-0 gap-0' : 'pb-5 gap-5'
			)}
		>
			{isRunBasedConversation ? (
				<>
					{runBuckets.map(bucket => {
						const isCollapsed = collapsedBuckets.has(bucket.label);
						return (
							<Fragment key={bucket.label}>
								<button
									type="button"
									className="pr-run-bucket-head"
									onClick={() => toggleBucket(bucket.label)}
								>
									<span className={cn('pr-run-bucket-chev', !isCollapsed && 'open')}>
										<ChevronRight className="h-3 w-3" />
									</span>
									<span className="pr-run-bucket-label">{bucket.label}</span>
									<span className="pr-run-bucket-count">
										{bucket.runs.length}{' '}
										{bucket.runs.length === 1
											? T('assistant.runSingular') || 'run'
											: T('assistant.runPlural') || 'runs'}
									</span>
								</button>
								{!isCollapsed &&
									bucket.runs.map(group => {
										const runId = groupRunId(group);
										// Prefer the run referenced by a message's agentRunId; fall
										// back to the run whose trigger marker matches this group
										// (routine runs link only via runTimestamp/runId).
										const run =
											(runId != null ? runsById?.get(runId) : undefined) ??
											(group.runTimestamp ? runsByMarker?.get(group.runTimestamp) : undefined);
										return (
											<RunSection
												key={group.runTimestamp || group.offset}
												runTimestamp={group.runTimestamp}
												messages={group.messages}
												runStatus={run?.status}
												runCostCredits={run?.costCredits}
												runStartedAt={run?.startedAt}
												runFinishedAt={run?.finishedAt}
												provenance={provenance}
												defaultExpanded={
													!userToggledRef.current && group.runTimestamp === lastRunTimestamp
												}
												conversationId={conversationId}
												avatarUrl={avatarUrl}
												avatarColor={avatarColor}
												assistantName={assistantName}
												globalMessageOffset={group.offset}
												disabled={!!sending && activeRunId === group.runTimestamp}
												streamingText={activeRunId === group.runTimestamp ? streamingText : ''}
												loading={activeRunId === group.runTimestamp && !!sending}
												toolStatus={toolStatus}
												streaming={!!sending && activeRunId === group.runTimestamp}
												onStop={onStop}
												onMultiActionUpdate={onMultiActionUpdate}
												onContactScrapeUpdate={onContactScrapeUpdate}
												onWriteProposalUpdate={onWriteProposalUpdate}
												onWriteProposalGroupItemUpdate={onWriteProposalGroupItemUpdate}
												onApprovalsGroupEmailItemUpdate={onApprovalsGroupEmailItemUpdate}
												onEmailDraftUpdate={onEmailDraftUpdate}
												onOfferDraftUpdate={onOfferDraftUpdate}
												onToolProposalUpdate={onToolProposalUpdate}
												onProspectingAddUpdate={onProspectingAddUpdate}
												onFeedback={handleFeedback}
												onRegenerate={onRegenerate}
												onSendFollowUp={onSendFollowUp}
												agents={agents}
												onAgentClick={onAgentClick}
												onTaskClick={onTaskClick}
												onRoutineClick={onRoutineClick}
												onOpenDelegation={onOpenDelegation}
											/>
										);
									})}
							</Fragment>
						);
					})}
					{runGroups.length === 0 && messages.length === 0 && (
						<div className="text-body-sm text-grey-8 text-center py-8">
							{T('assistant.noRunsYet') || 'No runs yet. The first run will appear here.'}
						</div>
					)}
				</>
			) : (
				(() => {
					// Cards from the current turn are hoisted out and rendered after
					// the streaming/loading section so they never appear above the
					// streaming reply. Any non-card messages (e.g. the assistant's
					// closing text reply, which can arrive after the proposal cards)
					// must still render inline at their original position.
					const trailingCardIdxSet = new Set(trailingCards.map(({ i }) => i));
					const inlineMessages = messages
						.map((m, i) => ({ m, i }))
						.filter(({ i }) => !trailingCardIdxSet.has(i))
						// `metadata.hidden` marks a generated `user` message that is
						// plumbing rather than something the rep typed — the post-meeting
						// seed, and the bulk-action completion prompt. RunSection has
						// honoured it since it was introduced; this path never did, so the
						// flag worked only in run-grouped conversations and an ordinary
						// chat still showed the raw prompt as a user bubble.
						.filter(({ m }) => !(m.metadata as { hidden?: boolean } | undefined)?.hidden);

					let prevDay: string | null = null;
					return inlineMessages.map(({ m: msg, i }) => {
						const msgDate = msg.regDate ? new Date(msg.regDate) : null;
						const msgDay = msgDate && !Number.isNaN(msgDate.getTime()) ? dayKey(msgDate) : null;
						const showDivider = !!msgDay && msgDay !== prevDay;
						if (msgDay) prevDay = msgDay;
						const dividerLabel = showDivider && msg.regDate ? formatDateDivider(msg.regDate) : '';
						return (
							<Fragment key={msg.id ?? i}>
								{showDivider && dividerLabel && (
									<div className="pr-date-divider flex items-center gap-3 text-body-sm uppercase tracking-[0.08em] text-grey-9">
										<span className="flex-1 h-px bg-border/60" />
										<span className="font-normal">{dividerLabel}</span>
										<span className="flex-1 h-px bg-border/60" />
									</div>
								)}
								{msg.chart ? (
									<ChartCard spec={msg.chart} />
								) : msg.prospectingFilters ? (
									<ProspectingFiltersCard panel={msg.prospectingFilters} />
								) : msg.prospectingCoverage ? (
									<CoverageCard
										panel={msg.prospectingCoverage}
										onAsk={onSendFollowUp ? text => onSendFollowUp(text, '') : undefined}
									/>
								) : (
									<>
										{msg.role === 'assistant' && msg.thinking && (
											<ThinkingBlock
												thinking={msg.thinking}
												isStreaming={false}
												defaultExpanded={i === lastAssistantMsgIdx && liveThinkingExpanded}
											/>
										)}
										{msg.role === 'assistant' && renderTurnMarker ? renderTurnMarker(msg) : null}
										{msg.role === 'assistant' && renderActivities
											? renderActivities(msg.activities, false)
											: null}
										{msg.role === 'assistant' && msg.untrustedSource ? (
											<UntrustedSourceNote source={msg.untrustedSource} />
										) : null}
										{/* WEB-11466: a delegated run's brief is stored with
										    role 'user', so without this it reads as something the
										    person wrote. */}
										{msg.role === 'user' && isHandoffSeed(msg.metadata) ? (
											<HandoffSeedMessage content={msg.content || ''} />
										) : msg.role === 'assistant' && !msg.content?.trim() ? null : (
											<MessageBlock
												role={msg.role}
												content={msg.content}
												images={msg.images}
												attachments={msg.attachments}
												accessory={msg.role === 'user' ? renderUserAccessory?.(msg) : undefined}
												regDate={msg.regDate}
												// Sticky diff open-state when the parent owns it (Pages);
												// otherwise fall back to "latest reply open".
												openDiff={
													openDiffIds
														? msg.id != null && openDiffIds.has(msg.id)
														: msg.role === 'assistant' && i === messages.length - 1
												}
												diffKey={msg.id}
												onDiffToggle={onDiffToggle}
												avatarUrl={msg.role === 'assistant' ? avatarUrl : undefined}
												avatarColor={msg.role === 'assistant' ? avatarColor : undefined}
												assistantName={msg.role === 'assistant' ? assistantName : undefined}
												agents={agents}
												onAgentClick={onAgentClick}
												onTaskClick={onTaskClick}
												onRoutineClick={onRoutineClick}
												headerAccessory={(() => {
													const comp = extractDelegationCompletion(msg.metadata);
													if (comp) {
														return (
															<DelegationResultHeader
																completion={comp}
																compact
																onOpen={d => onOpenDelegation?.(d)}
															/>
														);
													}
													return null;
												})()}
											/>
										)}
										{/* WEB-7850: a background task the user just approved is
										    surfaced as a prominent, click-to-open banner right below
										    the launching message — not a small header pill — so it's
										    unmistakable where they're already looking. Live status is
										    kept by the chip itself. */}
										{msg.role === 'assistant' &&
											extractDelegations(msg.metadata).map((d, di) => (
												<DelegationChip
													key={`del-${i}-${di}`}
													delegation={d}
													onOpen={d => onOpenDelegation?.(d)}
												/>
											))}
										{/* WEB-11465: the drafts the background run left, decided
										    here. The banner above still opens the run itself for
										    context; what it no longer does is make that the only
										    way to say yes. */}
										{msg.role === 'assistant' &&
											(() => {
												const ids = extractDelegationCompletion(
													msg.metadata
												)?.pendingApprovalIds;
												return ids?.length ? (
													<DelegatedApprovalCards approvalIds={ids} />
												) : null;
											})()}
										{/* Stacked proposal cards: a single turn (e.g. a
										    post-meeting completion) can produce an email AND
										    tasks AND a multi-action. Render every present
										    card so none get hidden. Email comes before the
										    writes group so the Approve all / Reject all
										    controls on the writes group are clearly attached
										    to the writes, not to the email below them. */}
										{msg.multiAction && (
											<MultiActionCard config={msg.multiAction} onUpdate={onMultiActionUpdate} />
										)}
										{msg.contactScrape && (
											<ContactScrapeCard
												config={msg.contactScrape}
												onUpdate={onContactScrapeUpdate}
											/>
										)}
										{msg.emailDraft && (
											<EmailDraftCard
												draft={msg.emailDraft}
												onUpdate={update => onEmailDraftUpdate?.(i, update)}
											/>
										)}
										{msg.offerDraft && (
											<OfferDraftCard
												draft={msg.offerDraft}
												onUpdate={update => onOfferDraftUpdate?.(i, update)}
											/>
										)}
										{msg.toolProposal && (
											<ToolProposalCard
												proposal={msg.toolProposal}
												onUpdate={update => onToolProposalUpdate?.(i, update)}
											/>
										)}
										{msg.prospectingAdd && (
											<ProspectingAddCard
												config={msg.prospectingAdd}
												onUpdate={(_id, update) => onProspectingAddUpdate?.(i, update)}
												defaultExpanded
											/>
										)}
										{msg.writeProposalGroup && (
											<WriteProposalGroupCard
												items={msg.writeProposalGroup.items}
												onWriteItemUpdate={(itemIdx, update) =>
													onWriteProposalGroupItemUpdate?.(i, itemIdx, update)
												}
												onEmailItemUpdate={(itemIdx, update) =>
													onApprovalsGroupEmailItemUpdate?.(i, itemIdx, update)
												}
												onAskAgent={askAgent}
											/>
										)}
										{msg.writeProposal && (
											<WriteCard
												proposal={msg.writeProposal}
												onUpdate={update => onWriteProposalUpdate?.(i, update)}
												onAskAgent={askAgent}
											/>
										)}
										{msg.role === 'assistant' && msg.content?.trim() && (
											<FeedbackInline
												messageIndex={i}
												conversationId={conversationId}
												onSubmit={handleFeedback}
												content={msg.content}
												onRegenerate={onRegenerate}
												disabled={sending}
											/>
										)}
									</>
								)}
							</Fragment>
						);
					});
				})()
			)}
			{!sending && !streamingText && onRetryLast && lastUserUnanswered && (
				<div className="pr-retry-row">
					<button
						type="button"
						className="pr-fbtn"
						onClick={() => onRetryLast()}
						aria-label={T('assistant.retry') || 'Retry'}
					>
						<RefreshCw className="h-4 w-4" />
					</button>
				</div>
			)}
			{!isRunBasedConversation &&
				(() => {
					const streamingBlock = (
						<Fragment key="streaming">
							{!activeRunId && renderActivities ? renderActivities(streamingActivities, true) : null}
							{streamingThinking && !activeRunId && (
								<ThinkingBlock
									thinking={streamingThinking}
									isStreaming={!!sending}
									expanded={liveThinkingExpanded}
									onToggle={setLiveThinkingExpanded}
								/>
							)}
							{(streamingText || streamingThinking) && !activeRunId && (
								<MessageBlock
									role="assistant"
									content={streamingText}
									streaming={!!streamingText}
									openDiffWhileStreaming={openDiffWhileStreaming}
									avatarUrl={avatarUrl}
									avatarColor={avatarColor}
									assistantName={assistantName}
									agents={agents}
									onAgentClick={onAgentClick}
									onTaskClick={onTaskClick}
									onRoutineClick={onRoutineClick}
								/>
							)}
							{sending && (streamingText || streamingThinking) && !activeRunId && (
								<div className="flex items-center gap-2 pl-14 pt-1">
									<span className="w-1 h-1 rounded-full bg-grey-8 animate-bounce [animation-delay:0ms]" />
									<span className="w-1 h-1 rounded-full bg-grey-8 animate-bounce [animation-delay:150ms]" />
									<span className="w-1 h-1 rounded-full bg-grey-8 animate-bounce [animation-delay:300ms]" />
									{toolStatus && (
										<span className="text-body-sm text-muted-foreground ml-1">{toolStatus}</span>
									)}
								</div>
							)}
							{sending && !streamingText && !streamingThinking && !activeRunId && (
								<LoadingBar
									status={toolStatus}
									avatarUrl={avatarUrl}
									avatarColor={avatarColor}
									assistantName={assistantName}
									inline
								/>
							)}
						</Fragment>
					);
					const cardsBlock = (
						<Fragment key="cards">
							{trailingCards.map(({ m: msg, i }) => (
								<Fragment key={msg.id ?? i}>
									{msg.multiAction && (
										<MultiActionCard config={msg.multiAction} onUpdate={onMultiActionUpdate} />
									)}
									{msg.contactScrape && (
										<ContactScrapeCard
											config={msg.contactScrape}
											onUpdate={onContactScrapeUpdate}
										/>
									)}
									{msg.emailDraft && (
										<EmailDraftCard
											draft={msg.emailDraft}
											onUpdate={update => onEmailDraftUpdate?.(i, update)}
										/>
									)}
									{msg.offerDraft && (
										<OfferDraftCard
											draft={msg.offerDraft}
											onUpdate={update => onOfferDraftUpdate?.(i, update)}
										/>
									)}
									{msg.toolProposal && (
										<ToolProposalCard
											proposal={msg.toolProposal}
											onUpdate={update => onToolProposalUpdate?.(i, update)}
										/>
									)}
									{msg.prospectingAdd && (
										<ProspectingAddCard
											config={msg.prospectingAdd}
											onUpdate={(_id, update) => onProspectingAddUpdate?.(i, update)}
											defaultExpanded
										/>
									)}
									{msg.writeProposalGroup && (
										<WriteProposalGroupCard
											items={msg.writeProposalGroup.items}
											onWriteItemUpdate={(itemIdx, update) =>
												onWriteProposalGroupItemUpdate?.(i, itemIdx, update)
											}
											onEmailItemUpdate={(itemIdx, update) =>
												onApprovalsGroupEmailItemUpdate?.(i, itemIdx, update)
											}
											onAskAgent={askAgent}
										/>
									)}
									{msg.writeProposal && (
										<WriteCard
											proposal={msg.writeProposal}
											onUpdate={update => onWriteProposalUpdate?.(i, update)}
											onAskAgent={askAgent}
										/>
									)}
								</Fragment>
							))}
						</Fragment>
					);
					return streamingRendersAfterCards ? (
						<>
							{cardsBlock}
							{streamingBlock}
						</>
					) : (
						<>
							{streamingBlock}
							{cardsBlock}
						</>
					);
				})()}
			{!isRunBasedConversation &&
				!sending &&
				!streamingText &&
				onSendFollowUp &&
				(() => {
					const last = messages[messages.length - 1];
					const replies = last?.role === 'assistant' ? last.suggestedReplies : undefined;
					if (!replies || replies.length === 0) return null;
					return <SuggestedReplies replies={replies} onSelect={text => onSendFollowUp(text, '')} />;
				})()}
		</div>
	);
};

export default MessageThread;
